import { Component, Input, Output, EventEmitter, inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AnnouncementService } from '../../../../core/services/announcement.service';
import { AlertService } from '../../../../shared/services/alert.service';
import { LookupService } from '../../../../core/services/lookup.service';
import { RejectReason, ComplaintReason } from '../../../../core/models/lookup.model';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-complaint-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="modal-backdrop" (click)="onClose.emit()">
      <div class="modal-card" (click)="$event.stopPropagation()">
        <div class="modal-header">
          <h3>Şikayət et</h3>
          <button class="close-btn" (click)="onClose.emit()">&times;</button>
        </div>

        <div class="modal-body">
          <div class="reasons-list">
            <div class="reason-item" *ngFor="let reason of reasons">
              <label>
                <input type="radio" name="reason" [value]="reason.id" [(ngModel)]="selectedReasonId">
                {{ reason.name }}
              </label>
            </div>
          </div>

          <div *ngIf="reasons.length === 0 && !error && !isLoading" style="text-align: center; color: #636e72;">
            Yüklənir...
          </div>

          <div class="comment-section" *ngIf="selectedReasonId === 1">
            <label>Əlavə qeyd:</label>
            <textarea [(ngModel)]="comment" name="comment" rows="3" placeholder="Şikayətiniz barədə ətraflı məlumat yazın"></textarea>
          </div>

          <div class="error-message" *ngIf="error">
            {{ error }}
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn-cancel" (click)="onClose.emit()">Ləğv et</button>
          <button class="btn-submit" [disabled]="!selectedReasonId || isLoading" (click)="submit()">
            {{ isLoading ? 'Göndərilir...' : 'Göndər' }}
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .modal-backdrop {
      position: fixed;
      top: 0; left: 0; width: 100%; height: 100%;
      background: rgba(0,0,0,0.5);
      z-index: 1000;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
    }
    .modal-card {
      background: white;
      width: 100%;
      max-width: 500px;
      border-radius: 16px;
      overflow: hidden;
    }
    .modal-header {
      padding: 20px;
      border-bottom: 1px solid #f1f2f6;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .modal-header h3 { margin: 0; font-size: 20px; }
    .close-btn { background: none; border: none; font-size: 24px; cursor: pointer; color: #b2bec3; }
    .modal-body { padding: 20px; display: flex; flex-direction: column; gap: 20px; }
    .reasons-list { display: flex; flex-direction: column; gap: 12px; }
    .reason-item label { display: flex; gap: 12px; align-items: flex-start; cursor: pointer; font-size: 15px; color: #2d3436; }
    .reason-item input { margin-top: 4px; }
    .comment-section { display: flex; flex-direction: column; gap: 8px; }
    .comment-section label { font-size: 14px; font-weight: 600; color: #636e72; }
    .comment-section textarea {
      width: 100%;
      padding: 12px;
      border: 1px solid #dfe6e9;
      border-radius: 8px;
      font-size: 15px;
      resize: vertical;
    }
    .modal-footer {
      padding: 20px;
      border-top: 1px solid #f1f2f6;
      display: flex;
      justify-content: flex-end;
      gap: 12px;
    }
    .btn-cancel { padding: 12px 24px; border: none; background: #f1f2f6; color: #2d3436; border-radius: 8px; font-weight: 600; cursor: pointer; }
    .btn-submit { padding: 12px 24px; border: none; background: #d63031; color: white; border-radius: 8px; font-weight: 600; cursor: pointer; }
    .btn-submit:disabled { opacity: 0.6; cursor: not-allowed; }
    .error-message { color: #d63031; font-size: 14px; text-align: center; }
  `]
})
export class ComplaintModalComponent implements OnInit {
  @Input() listingId!: string;
  @Output() onClose = new EventEmitter<void>();

  private announcementService = inject(AnnouncementService);
  private alertService = inject(AlertService);
  private lookupService = inject(LookupService);
  private cdr = inject(ChangeDetectorRef);

  reasons: ComplaintReason[] = [];

  selectedReasonId: number | null = null;
  comment: string = '';
  isLoading = false;
  error: string | null = null;

  ngOnInit() {
    this.loadReasons();
  }

  loadReasons() {
    this.lookupService.getComplaintReasons().subscribe({
      next: (data) => {
        if (data) {
          // Put "Digər" (id: 1) at the end
          const otherReason = data.find(r => r.id === 1);
          const otherReasons = data.filter(r => r.id !== 1);
          this.reasons = otherReason ? [...otherReasons, otherReason] : data;
        } else {
          this.reasons = [];
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.error = 'Şikayət səbəblərini yükləmək mümkün olmadı.';
        this.cdr.detectChanges();
      }
    });
  }

  submit() {
    if (!this.selectedReasonId) return;

    // Digər seçilməyibsə comment göndərilməsin
    const submissionComment = this.selectedReasonId === 1 ? this.comment : '';

    this.isLoading = true;
    this.error = null;

    this.announcementService.reportAnnouncement(this.listingId, this.selectedReasonId, submissionComment)
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: (success) => {
          if (success) {
            this.alertService.success('Şikayətiniz göndərildi.');
            this.onClose.emit();
          } else {
            this.error = 'Şikayət göndərmək mümkün olmadı.';
          }
        },
        error: () => {
          this.error = 'Server xətası baş verdi.';
        }
      });
  }
}
